import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Base documentation page wrapper
export const DocsPage = styled.div`
  max-width: 100%;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  h1 {
    font-family: 'Inter', sans-serif;
    font-size: 2.75rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.75rem;
    line-height: 1.15;
    letter-spacing: -0.02em;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.75rem;
    }
  }
  
  .subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 1.15rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.6;
    margin-bottom: 2.5rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  h2 {
    font-family: 'Inter', sans-serif;
    font-size: 1.75rem;
    font-weight: 700;
    color: #ffffff;
    margin: 3rem 0 1rem 0;
    line-height: 1.25;
    letter-spacing: -0.01em;
    scroll-margin-top: 100px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.25rem;
    }
  }
  
  h3 {
    font-family: 'Inter', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: #ffffff;
    margin: 2rem 0 0.75rem 0;
    line-height: 1.35;
    letter-spacing: -0.01em;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }
  
  h4 {
    font-family: 'Inter', sans-serif;
    font-size: 1.05rem;
    font-weight: 600;
    color: #ffffff;
    margin: 1.5rem 0 0.5rem 0;
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  
  p {
    font-family: 'Inter', sans-serif;
    font-size: 0.9375rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 1.25rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  
  ul, ol {
    margin: 1.25rem 0;
    padding-left: 1.75rem;
    
    @media (max-width: 480px) {
      padding-left: 1.25rem;
    }
  }
  
  li {
    font-family: 'Inter', sans-serif;
    font-size: 0.9375rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 0.5rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    &::marker {
      color: rgba(168, 85, 247, 0.7);
    }
  }
  
  strong {
    font-weight: 600;
    color: #ffffff;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  
  a {
    color: #a855f7;
    text-decoration: none;
    transition: color 0.2s ease;
    word-break: break-word;
    overflow-wrap: break-word;
    
    &:hover {
      color: #c084fc;
      text-decoration: underline;
    }
  }
  
  code {
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 0.875rem;
    background: rgba(168, 85, 247, 0.1);
    color: #c084fc;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid rgba(168, 85, 247, 0.2);
    word-break: break-word;
    white-space: pre-wrap;
    max-width: 100%;
    display: inline-block;
  }
  
  blockquote {
    border-left: 3px solid rgba(168, 85, 247, 0.5);
    margin: 1.5rem 0;
    padding-left: 1.25rem;
    font-style: italic;
    color: rgba(255, 255, 255, 0.65);
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    @media (max-width: 480px) {
      padding-left: 1rem;
    }
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5rem 0;
    font-size: 0.9375rem;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    @media (max-width: 768px) {
      font-size: 0.875rem;
    }
  }
  
  th, td {
    text-align: left;
    padding: 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    @media (max-width: 480px) {
      padding: 0.5rem;
    }
  }
  
  th {
    font-weight: 600;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.02);
  }
  
  td {
    color: rgba(255, 255, 255, 0.75);
  }
  
  hr {
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    margin: 2.5rem 0;
  }
`;

// Hero section with title and subtitle
export const HeroSection = styled.div`
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

// Feature grid for card layouts
export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin: 2rem 0;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

// Individual feature card
export const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 1.25rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(168, 85, 247, 0.3);
    transform: translateY(-2px);
  }
  
  .icon {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
    display: block;
  }
  
  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    font-size: 0.875rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.65);
    margin: 0;
  }
`;

// Info/Warning/Success boxes
export const InfoBox = styled.div<{ variant?: 'info' | 'success' | 'warning' | 'purple' }>`
  background: ${(props: { variant?: string }) => {
    switch (props.variant) {
      case 'success': return 'rgba(16, 185, 129, 0.08)';
      case 'warning': return 'rgba(245, 158, 11, 0.08)';
      case 'purple': return 'rgba(168, 85, 247, 0.08)';
      default: return 'rgba(59, 130, 246, 0.08)';
    }
  }};
  border: 1px solid ${(props: { variant?: string }) => {
    switch (props.variant) {
      case 'success': return 'rgba(16, 185, 129, 0.2)';
      case 'warning': return 'rgba(245, 158, 11, 0.2)';
      case 'purple': return 'rgba(168, 85, 247, 0.2)';
      default: return 'rgba(59, 130, 246, 0.2)';
    }
  }};
  border-radius: 8px;
  padding: 1.25rem;
  margin: 1.5rem 0;
  
  .info-title {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    color: ${(props: { variant?: string }) => {
      switch (props.variant) {
        case 'success': return '#10b981';
        case 'warning': return '#f59e0b';
        case 'purple': return '#a855f7';
        default: return '#3b82f6';
      }
    }};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.75rem;
  }
  
  p {
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 0.75rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul, ol {
    margin: 0.75rem 0 0 0;
  }
`;

// Code block with syntax highlighting
export const CodeBlock = styled.pre`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 1.25rem;
  margin: 1.5rem 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  max-width: 100%;
  box-sizing: border-box;
  word-wrap: normal;
  white-space: pre;
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.8125rem;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 247, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(168, 85, 247, 0.5);
  }
  
  code {
    background: transparent;
    border: none;
    padding: 0;
    color: inherit;
    font-size: inherit;
    white-space: pre;
    word-wrap: normal;
  }
`;

// Steps list with numbered items
export const StepsList = styled.ol`
  counter-reset: step-counter;
  list-style: none;
  padding-left: 0;
  margin: 1.5rem 0;
  
  li {
    counter-increment: step-counter;
    position: relative;
    padding-left: 3rem;
    margin-bottom: 1.5rem;
    
    &::before {
      content: counter(step-counter);
      position: absolute;
      left: 0;
      top: 0;
      width: 32px;
      height: 32px;
      background: linear-gradient(135deg, rgba(168, 85, 247, 0.9), rgba(147, 51, 234, 0.9));
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.875rem;
      color: #ffffff;
    }
    
    strong {
      display: block;
      margin-bottom: 0.25rem;
      font-size: 1rem;
    }
  }
`;

// Callout card for CTAs
export const CalloutCard = styled.div`
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(147, 51, 234, 0.05));
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 12px;
  padding: 2rem;
  margin: 3rem 0;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 2rem 0;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem;
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 0 0 0.75rem 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }
  
  p {
    font-size: 1rem;
    margin-bottom: 1.5rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 0.9375rem;
    }
  }
`;

// CTA Button
export const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.9), rgba(168, 85, 247, 0.9));
  color: #ffffff;
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 8px;
  padding: 12px 28px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(168, 85, 247, 0.35);
    border-color: rgba(168, 85, 247, 0.5);
    text-decoration: none;
    color: #ffffff;
  }
`;

// Network badge for displaying network info
export const NetworkBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
  margin-right: 0.75rem;
  margin-bottom: 0.75rem;
  
  .label {
    color: rgba(255, 255, 255, 0.5);
    font-weight: 400;
  }
  
  .value {
    color: #ffffff;
    font-weight: 600;
  }
`;

// Section divider
export const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin: 3rem 0;
`;

// Two-column layout
export const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

// Info card for displaying detailed information
export const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 1.5rem;
  
  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 1rem 0;
  }
  
  p {
    font-size: 0.9375rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 0.75rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul {
    margin: 0;
    padding-left: 1.5rem;
  }
  
  li {
    font-size: 0.9375rem;
    margin-bottom: 0.5rem;
  }
`;

// Badge component for tags/labels
export const Badge = styled.span<{ variant?: 'default' | 'success' | 'warning' | 'purple' }>`
  display: inline-flex;
  align-items: center;
  background: ${(props: { variant?: string }) => {
    switch (props.variant) {
      case 'success': return 'rgba(16, 185, 129, 0.15)';
      case 'warning': return 'rgba(245, 158, 11, 0.15)';
      case 'purple': return 'rgba(168, 85, 247, 0.15)';
      default: return 'rgba(255, 255, 255, 0.08)';
    }
  }};
  color: ${(props: { variant?: string }) => {
    switch (props.variant) {
      case 'success': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'purple': return '#a855f7';
      default: return 'rgba(255, 255, 255, 0.85)';
    }
  }};
  border: 1px solid ${(props: { variant?: string }) => {
    switch (props.variant) {
      case 'success': return 'rgba(16, 185, 129, 0.3)';
      case 'warning': return 'rgba(245, 158, 11, 0.3)';
      case 'purple': return 'rgba(168, 85, 247, 0.3)';
      default: return 'rgba(255, 255, 255, 0.15)';
    }
  }};
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

// Table wrapper for responsive tables
export const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 1.5rem 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  max-width: 100%;
  box-sizing: border-box;
  
  table {
    margin: 0;
    min-width: 600px;
    
    @media (max-width: 768px) {
      min-width: 500px;
    }
  }
`;

// Collapsible section (can be enhanced with state management)
export const CollapsibleSection = styled.details`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  
  summary {
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    user-select: none;
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    &::-webkit-details-marker {
      display: none;
    }
    
    &::after {
      content: '▼';
      font-size: 0.75rem;
      color: rgba(168, 85, 247, 0.7);
      transition: transform 0.2s ease;
    }
  }
  
  &[open] summary::after {
    transform: rotate(180deg);
  }
  
  .content {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

